import { useReducer } from 'react';
import { Formik, Form, Field, FieldProps, FormikErrors } from 'formik';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import {
  Heading,
  Input,
  Text,
  Box,
  LoadableButton,
  Button,
  ButtonGroup,
  Label,
  Alert,
  Flex
} from '../../components';
import { I18NSave, I18NCancel } from '../../components/common-i18n';
import {
  updatePinCodeMutation,
  updatePinCodeMutationVariables,
  resetPinCodeMutation
} from '../../core-types';
import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client';
import { useCurrentUser } from '../../lib/core/useCurrentUser';

type FormValues = {
  pinCode: string;
};

const PIN_CODE_LENGTH = 4;

const UPDATE_PIN_CODE = gql`
  mutation updatePinCodeMutation($pinCode: String!) {
    updatePinCode(pinCode: $pinCode)
  }
`;
const RESET_PIN_CODE = gql`
  mutation resetPinCodeMutation {
    resetPinCode
  }
`;

const messages = defineMessages({
  newPinError: {
    id: 'modules.Profile.PinCode.newPinError',
    defaultMessage: 'Please enter a {pinCodeLength} digit code'
  },
  confirmResetPin: {
    id: 'modules.Profile.PinCode.confirmResetPin',
    defaultMessage: 'Do you want to remove your pin kode?'
  }
});

function reducer(state, action) {
  switch (action.type) {
    case 'IS_EDITING':
      return { isEditing: true, updateSuccess: false, resetSuccess: false };
    case 'CANCEL_EDITING':
      return { isEditing: false, updateSuccess: false, resetSuccess: false };
    case 'UPDATE_SUCCESS':
      return { isEditing: false, updateSuccess: true, resetSuccess: false };
    case 'RESET_SUCCESS':
      return { isEditing: false, updateSuccess: false, resetSuccess: true };
    default:
      throw new Error('Unknown action type');
  }
}

function PinCode() {
  const intl = useIntl();
  const { currentUser, refetch } = useCurrentUser();
  const [state, dispatch] = useReducer(reducer, {
    isEditing: false,
    updateSuccess: false,
    resetSuccess: false
  });
  const [resetPinCode, resetPinCodeResult] = useMutation<resetPinCodeMutation>(
    RESET_PIN_CODE,
    {
      onCompleted: () => {
        refetch();
        dispatch({ type: 'RESET_SUCCESS' });
      }
    }
  );
  const [updatePinCode, updatePinCodeResult] = useMutation<
    updatePinCodeMutation,
    updatePinCodeMutationVariables
  >(UPDATE_PIN_CODE, {
    onCompleted: () => {
      refetch();
      dispatch({ type: 'UPDATE_SUCCESS' });
    }
  });
  const { isEditing, updateSuccess, resetSuccess } = state;
  if (!currentUser) {
    return null;
  }
  const { havePinCode } = currentUser;
  return (
    <div data-testid="Profile__PinCode">
      <Heading textAlign="center">
        <FormattedMessage id="pin_code" defaultMessage="PIN code" />
      </Heading>
      <Box mb={6}>
        <Text as="p">
          <FormattedMessage
            id="modules.Profile.PinCode.intro1"
            defaultMessage="Some bike stations has a display. From these you can unlock a bike by entering your phone number and a four-digit code you create below. Keep in mind that the PIN code is personal."
          />
        </Text>
        <Text as="p">
          <FormattedMessage
            id="modules.Profile.PinCode.intro2"
            defaultMessage="To make sure your code is safe, we’ll deactivate your account if we suspect that someone else has been using it."
          />
        </Text>
      </Box>
      {updateSuccess && (
        <FormattedMessage
          id="modules.Profile.PinCode.updateSuccess"
          defaultMessage="Your pin code have been updated!"
        >
          {(txt: string) => (
            <Alert data-testid="Profile__PinCode__Updated">{txt}</Alert>
          )}
        </FormattedMessage>
      )}
      {resetSuccess && (
        <Alert data-testid="Profile__PinCode__Reset">
          <FormattedMessage
            id="modules.Profile.PinCode.resetSuccess"
            defaultMessage="Your pin code have removed!"
          />
        </Alert>
      )}
      {!isEditing && (
        <Flex justifyContent="center">
          <ButtonGroup>
            <Button
              shape="cool"
              variant="primary"
              onClick={() => {
                dispatch({ type: 'IS_EDITING' });
              }}
            >
              {havePinCode ? (
                <FormattedMessage
                  id="modules.Profile.PinCode.changePin"
                  defaultMessage="Change pin"
                />
              ) : (
                <FormattedMessage
                  id="modules.Profile.PinCode.setPin"
                  defaultMessage="Set pin"
                />
              )}
            </Button>
            {havePinCode && (
              <LoadableButton
                shape="cool"
                loading={resetPinCodeResult.loading}
                responseState={resetPinCodeResult.error ? 'error' : 'success'}
                onClick={() => {
                  if (confirm(intl.formatMessage(messages.confirmResetPin))) {
                    resetPinCode();
                  }
                }}
              >
                <FormattedMessage
                  id="modules.Profile.PinCode.resetPin"
                  defaultMessage="Remove pin"
                />
              </LoadableButton>
            )}
          </ButtonGroup>
        </Flex>
      )}
      {isEditing && (
        <Formik<FormValues>
          validate={values => {
            const errors: FormikErrors<FormValues> = {};

            if (!values.pinCode || values.pinCode.length !== 4) {
              errors.pinCode = intl.formatMessage(messages.newPinError, {
                pinCodeLength: PIN_CODE_LENGTH
              });
            }

            return errors;
          }}
          initialValues={{ pinCode: '' }}
          onSubmit={values => {
            updatePinCode({
              variables: { pinCode: values.pinCode }
            });
          }}
        >
          {({ errors, touched, submitCount }) => {
            return (
              <Form>
                <Box ml="auto" mr="auto" style={{ maxWidth: '250px' }}>
                  {submitCount > 0 && touched.pinCode && errors.pinCode && (
                    <Alert variant="error">{errors.pinCode}</Alert>
                  )}
                  <Label htmlFor="input-pin-code">
                    <FormattedMessage
                      id="modules.Profile.PinCode.newPinLabel"
                      defaultMessage="New PIN, {pinCodeLength} digits"
                      values={{ pinCodeLength: PIN_CODE_LENGTH }}
                    />
                  </Label>
                  <Field
                    name="pinCode"
                    render={({ field }: FieldProps) => {
                      return (
                        <Box mb={4}>
                          <Input
                            id="input-pin-code"
                            data-testid="Profile__PinCode__Input"
                            type="tel"
                            autoFocus
                            {...field}
                          />
                        </Box>
                      );
                    }}
                  />
                  <Flex justifyContent="center">
                    <LoadableButton
                      data-testid="Profile__PinCode__SubmitButton"
                      loading={updatePinCodeResult.loading}
                      responseState={
                        updatePinCodeResult.error ? 'error' : 'success'
                      }
                      type="submit"
                      variant="primary"
                      shape="cool"
                    >
                      <I18NSave />
                    </LoadableButton>
                  </Flex>
                  <Flex justifyContent="center">
                    <Button
                      variant="link"
                      onClick={() => dispatch({ type: 'CANCEL_EDITING' })}
                    >
                      <I18NCancel />
                    </Button>
                  </Flex>
                </Box>
              </Form>
            );
          }}
        </Formik>
      )}
    </div>
  );
}

export default PinCode;
